import {AmplitudeFavouriteLocation, AmplitudeFavouritePlacement, IAmplitudeFavouriteListContext} from "../tracking/get_amplitude_favourites_event_properties";

export type FavouritesTopbarPendingVariant = "desktop" | "mobile";

export interface IPendingFavouritePropertyIntent {
    id: number;
    offerId?: number;
    placement?: AmplitudeFavouritePlacement;
    location?: AmplitudeFavouriteLocation;
    listContext?: IAmplitudeFavouriteListContext;
}

export interface IPendingFavouriteOfferIntent {
    id: number;
    placement?: AmplitudeFavouritePlacement;
    location?: AmplitudeFavouriteLocation;
}

export const PENDING_FAVOURITES_TTL_MS = 30 * 60 * 1000;

interface IPendingIdEntry {
    id: number;
    createdAt: number;
}

interface IStoredPropertyIntent extends IPendingFavouritePropertyIntent {
    createdAt: number;
}

const TOPBAR_KEY = "rp_pending_favourites_topbar";
const COMPARISON_KEY = "rp_pending_in_comparison_property_ids";
const FAVOURITE_PROPERTY_KEY = "rp_pending_favourite_property_ids";
const FAVOURITE_OFFER_KEY = "rp_pending_favourite_offer_ids";

const getSessionStorage = (): Storage | null => {
    try {
        if (typeof window === "undefined") {
            return null;
        }

        return window.sessionStorage;
    } catch {
        return null;
    }
};

const safeGetItem = (key: string): string | null => {
    try {
        return getSessionStorage()?.getItem(key) ?? null;
    } catch {
        return null;
    }
};

const safeSetItem = (key: string, value: string) => {
    try {
        getSessionStorage()?.setItem(key, value);
    } catch {
        return;
    }
};

const safeRemoveItem = (key: string) => {
    try {
        getSessionStorage()?.removeItem(key);
    } catch {
        return;
    }
};

const isPendingExpired = (createdAt: number, now: number, maxAgeMs = PENDING_FAVOURITES_TTL_MS) => {
    return now - createdAt >= maxAgeMs;
};

const parseIdEntry = (item: unknown, now: number): IPendingIdEntry | null => {
    if (typeof item === "number") {
        return {id: item, createdAt: now};
    }

    if (item && typeof item === "object" && typeof (item as {id?: unknown}).id === "number") {
        const entry = item as {id: number; createdAt?: unknown};

        return {
            id: entry.id,
            createdAt: typeof entry.createdAt === "number" ? entry.createdAt : now
        };
    }

    return null;
};

const readIdEntries = (key: string): IPendingIdEntry[] => {
    const value = safeGetItem(key);

    if (!value) {
        return [];
    }

    try {
        const parsed: unknown = JSON.parse(value);

        if (!Array.isArray(parsed)) {
            return [];
        }

        const now = Date.now();

        return parsed.flatMap((item) => {
            const entry = parseIdEntry(item, now);

            return entry ? [entry] : [];
        });
    } catch {
        return [];
    }
};

const addIdToList = (key: string, id: number) => {
    const now = Date.now();
    const entries = readIdEntries(key).filter((entry) => !isPendingExpired(entry.createdAt, now));

    if (entries.some((entry) => entry.id === id)) {
        return;
    }

    safeSetItem(key, JSON.stringify([...entries, {id, createdAt: now}]));
};

const removeIdFromList = (key: string, id: number) => {
    const now = Date.now();
    const entries = readIdEntries(key).filter((entry) => entry.id !== id && !isPendingExpired(entry.createdAt, now));

    if (entries.length === 0) {
        safeRemoveItem(key);
        return;
    }

    safeSetItem(key, JSON.stringify(entries));
};

const consumeIdList = (key: string, maxAgeMs?: number): number[] => {
    const now = Date.now();
    const entries = readIdEntries(key);

    safeRemoveItem(key);

    return entries.filter((entry) => maxAgeMs == null || !isPendingExpired(entry.createdAt, now, maxAgeMs)).map((entry) => entry.id);
};

export const setPendingFavouritesTopbar = (variant: FavouritesTopbarPendingVariant) => {
    safeSetItem(TOPBAR_KEY, variant);
};

export const consumePendingFavouritesTopbar = (variant: FavouritesTopbarPendingVariant) => {
    if (safeGetItem(TOPBAR_KEY) !== variant) {
        return false;
    }

    safeRemoveItem(TOPBAR_KEY);

    return true;
};

export const clearPendingFavouritesTopbar = () => {
    safeRemoveItem(TOPBAR_KEY);
};

export const setPendingComparisonPropertyId = (propertyId: number) => {
    addIdToList(COMPARISON_KEY, propertyId);
};

export const removePendingComparisonPropertyId = (propertyId: number) => {
    removeIdFromList(COMPARISON_KEY, propertyId);
};

export const consumePendingComparisonPropertyIds = (maxAgeMs?: number): number[] => {
    return consumeIdList(COMPARISON_KEY, maxAgeMs);
};

const readListContext = (value: unknown): IAmplitudeFavouriteListContext | undefined => {
    if (!value || typeof value !== "object") {
        return undefined;
    }

    const list = value as IAmplitudeFavouriteListContext;
    const listContext: IAmplitudeFavouriteListContext = {
        ...(typeof list.itemListId === "string" ? {itemListId: list.itemListId} : {}),
        ...(typeof list.itemListName === "string" || Array.isArray(list.itemListName) ? {itemListName: list.itemListName} : {}),
        ...(list.listView === "floor_plans" || list.listView === "list" ? {listView: list.listView} : {}),
        ...(typeof list.index === "number" ? {index: list.index} : {})
    };

    return Object.keys(listContext).length > 0 ? listContext : undefined;
};

const toPublicPropertyIntent = (intent: IStoredPropertyIntent): IPendingFavouritePropertyIntent => {
    return {
        id: intent.id,
        ...(typeof intent.offerId === "number" ? {offerId: intent.offerId} : {}),
        ...(intent.placement ? {placement: intent.placement} : {}),
        ...(intent.location ? {location: intent.location} : {}),
        ...(intent.listContext ? {listContext: intent.listContext} : {})
    };
};

const parsePropertyIntents = (parsed: unknown, now: number): IStoredPropertyIntent[] => {
    if (!Array.isArray(parsed)) {
        return [];
    }

    return parsed.flatMap((item) => {
        if (typeof item === "number") {
            return [{id: item, createdAt: now}];
        }

        if (item && typeof item === "object" && typeof (item as {id?: unknown}).id === "number") {
            const intent = item as IPendingFavouritePropertyIntent & {createdAt?: unknown};
            const listContext = readListContext(intent.listContext);

            return [
                {
                    id: intent.id,
                    createdAt: typeof intent.createdAt === "number" ? intent.createdAt : now,
                    ...(typeof intent.offerId === "number" ? {offerId: intent.offerId} : {}),
                    ...(intent.placement ? {placement: intent.placement} : {}),
                    ...(intent.location ? {location: intent.location} : {}),
                    ...(listContext ? {listContext} : {})
                }
            ];
        }

        return [];
    });
};

const readIntents = (key: string): IStoredPropertyIntent[] => {
    const value = safeGetItem(key);

    if (!value) {
        return [];
    }

    try {
        return parsePropertyIntents(JSON.parse(value), Date.now());
    } catch {
        return [];
    }
};

const setPendingIntent = (key: string, id: number, meta?: Omit<IPendingFavouritePropertyIntent, "id">) => {
    const now = Date.now();
    const intents = readIntents(key).filter((intent) => !isPendingExpired(intent.createdAt, now));

    if (intents.some((intent) => intent.id === id)) {
        return;
    }

    safeSetItem(key, JSON.stringify([...intents, {id, ...meta, createdAt: now}]));
};

const consumePendingIntents = (key: string, maxAgeMs?: number): IPendingFavouritePropertyIntent[] => {
    const now = Date.now();
    const intents = readIntents(key);

    safeRemoveItem(key);

    return intents.filter((intent) => maxAgeMs == null || !isPendingExpired(intent.createdAt, now, maxAgeMs)).map(toPublicPropertyIntent);
};

export const setPendingFavouritePropertyId = (propertyId: number, meta?: Omit<IPendingFavouritePropertyIntent, "id">) => {
    setPendingIntent(FAVOURITE_PROPERTY_KEY, propertyId, meta);
};

export const consumePendingFavouritePropertyIntents = (maxAgeMs?: number): IPendingFavouritePropertyIntent[] => {
    return consumePendingIntents(FAVOURITE_PROPERTY_KEY, maxAgeMs);
};

export const consumePendingFavouritePropertyIds = (maxAgeMs?: number): number[] => {
    return consumePendingFavouritePropertyIntents(maxAgeMs).map((intent) => intent.id);
};

export const setPendingFavouriteOfferId = (offerId: number, meta?: Omit<IPendingFavouriteOfferIntent, "id">) => {
    setPendingIntent(FAVOURITE_OFFER_KEY, offerId, meta);
};

export const consumePendingFavouriteOfferIntents = (maxAgeMs?: number): IPendingFavouriteOfferIntent[] => {
    return consumePendingIntents(FAVOURITE_OFFER_KEY, maxAgeMs).map((intent) => ({
        id: intent.id,
        ...(intent.placement ? {placement: intent.placement} : {}),
        ...(intent.location ? {location: intent.location} : {})
    }));
};

export const consumePendingFavouriteOfferIds = (maxAgeMs?: number): number[] => {
    return consumePendingFavouriteOfferIntents(maxAgeMs).map((intent) => intent.id);
};

export const redirectToFavouritesList = (url: string) => {
    window.location.assign(url);
};
import {AmplitudeFavouriteLocation, AmplitudeFavouritePlacement, IAmplitudeFavouriteListContext} from "../tracking/get_amplitude_favourites_event_properties";

export type FavouritesTopbarPendingVariant = "desktop" | "mobile";

export interface IPendingFavouritePropertyIntent {
    id: number;
    offerId?: number;
    placement?: AmplitudeFavouritePlacement;
    location?: AmplitudeFavouriteLocation;
    listContext?: IAmplitudeFavouriteListContext;
}

export interface IPendingFavouriteOfferIntent {
    id: number;
    placement?: AmplitudeFavouritePlacement;
    location?: AmplitudeFavouriteLocation;
}

export const PENDING_FAVOURITES_TTL_MS = 30 * 60 * 1000;

interface IPendingIdEntry {
    id: number;
    createdAt: number;
}

interface IStoredPropertyIntent extends IPendingFavouritePropertyIntent {
    createdAt: number;
}

const TOPBAR_KEY = "rp_pending_favourites_topbar";
const COMPARISON_KEY = "rp_pending_in_comparison_property_ids";
const FAVOURITE_PROPERTY_KEY = "rp_pending_favourite_property_ids";
const FAVOURITE_OFFER_KEY = "rp_pending_favourite_offer_ids";

const getSessionStorage = (): Storage | null => {
    try {
        if (typeof window === "undefined") {
            return null;
        }

        return window.sessionStorage;
    } catch {
        return null;
    }
};

const safeGetItem = (key: string): string | null => {
    try {
        return getSessionStorage()?.getItem(key) ?? null;
    } catch {
        return null;
    }
};

const safeSetItem = (key: string, value: string) => {
    try {
        getSessionStorage()?.setItem(key, value);
    } catch {
        return;
    }
};

const safeRemoveItem = (key: string) => {
    try {
        getSessionStorage()?.removeItem(key);
    } catch {
        return;
    }
};

const isPendingExpired = (createdAt: number, now: number, maxAgeMs = PENDING_FAVOURITES_TTL_MS) => {
    return now - createdAt >= maxAgeMs;
};

const parseIdEntry = (item: unknown, now: number): IPendingIdEntry | null => {
    if (typeof item === "number") {
        return {id: item, createdAt: now};
    }

    if (item && typeof item === "object" && typeof (item as {id?: unknown}).id === "number") {
        const entry = item as {id: number; createdAt?: unknown};

        return {
            id: entry.id,
            createdAt: typeof entry.createdAt === "number" ? entry.createdAt : now
        };
    }

    return null;
};

const readIdEntries = (key: string): IPendingIdEntry[] => {
    const value = safeGetItem(key);

    if (!value) {
        return [];
    }

    try {
        const parsed: unknown = JSON.parse(value);

        if (!Array.isArray(parsed)) {
            return [];
        }

        const now = Date.now();

        return parsed.flatMap((item) => {
            const entry = parseIdEntry(item, now);

            return entry ? [entry] : [];
        });
    } catch {
        return [];
    }
};

const addIdToList = (key: string, id: number) => {
    const now = Date.now();
    const entries = readIdEntries(key).filter((entry) => !isPendingExpired(entry.createdAt, now));

    if (entries.some((entry) => entry.id === id)) {
        return;
    }

    safeSetItem(key, JSON.stringify([...entries, {id, createdAt: now}]));
};

const removeIdFromList = (key: string, id: number) => {
    const now = Date.now();
    const entries = readIdEntries(key).filter((entry) => entry.id !== id && !isPendingExpired(entry.createdAt, now));

    if (entries.length === 0) {
        safeRemoveItem(key);
        return;
    }

    safeSetItem(key, JSON.stringify(entries));
};

const consumeIdList = (key: string, maxAgeMs?: number): number[] => {
    const now = Date.now();
    const entries = readIdEntries(key);

    safeRemoveItem(key);

    return entries.filter((entry) => maxAgeMs == null || !isPendingExpired(entry.createdAt, now, maxAgeMs)).map((entry) => entry.id);
};

export const setPendingFavouritesTopbar = (variant: FavouritesTopbarPendingVariant) => {
    safeSetItem(TOPBAR_KEY, variant);
};

export const consumePendingFavouritesTopbar = (variant: FavouritesTopbarPendingVariant) => {
    if (safeGetItem(TOPBAR_KEY) !== variant) {
        return false;
    }

    safeRemoveItem(TOPBAR_KEY);

    return true;
};

export const clearPendingFavouritesTopbar = () => {
    safeRemoveItem(TOPBAR_KEY);
};

export const setPendingComparisonPropertyId = (propertyId: number) => {
    addIdToList(COMPARISON_KEY, propertyId);
};

export const removePendingComparisonPropertyId = (propertyId: number) => {
    removeIdFromList(COMPARISON_KEY, propertyId);
};

export const consumePendingComparisonPropertyIds = (maxAgeMs?: number): number[] => {
    return consumeIdList(COMPARISON_KEY, maxAgeMs);
};

const readListContext = (value: unknown): IAmplitudeFavouriteListContext | undefined => {
    if (!value || typeof value !== "object") {
        return undefined;
    }

    const list = value as IAmplitudeFavouriteListContext;
    const listContext: IAmplitudeFavouriteListContext = {
        ...(typeof list.itemListId === "string" ? {itemListId: list.itemListId} : {}),
        ...(typeof list.itemListName === "string" || Array.isArray(list.itemListName) ? {itemListName: list.itemListName} : {}),
        ...(list.listView === "floor_plans" || list.listView === "list" ? {listView: list.listView} : {}),
        ...(typeof list.index === "number" ? {index: list.index} : {})
    };

    return Object.keys(listContext).length > 0 ? listContext : undefined;
};

const toPublicPropertyIntent = (intent: IStoredPropertyIntent): IPendingFavouritePropertyIntent => {
    return {
        id: intent.id,
        ...(typeof intent.offerId === "number" ? {offerId: intent.offerId} : {}),
        ...(intent.placement ? {placement: intent.placement} : {}),
        ...(intent.location ? {location: intent.location} : {}),
        ...(intent.listContext ? {listContext: intent.listContext} : {})
    };
};

const parsePropertyIntents = (parsed: unknown, now: number): IStoredPropertyIntent[] => {
    if (!Array.isArray(parsed)) {
        return [];
    }

    return parsed.flatMap((item) => {
        if (typeof item === "number") {
            return [{id: item, createdAt: now}];
        }

        if (item && typeof item === "object" && typeof (item as {id?: unknown}).id === "number") {
            const intent = item as IPendingFavouritePropertyIntent & {createdAt?: unknown};
            const listContext = readListContext(intent.listContext);

            return [
                {
                    id: intent.id,
                    createdAt: typeof intent.createdAt === "number" ? intent.createdAt : now,
                    ...(typeof intent.offerId === "number" ? {offerId: intent.offerId} : {}),
                    ...(intent.placement ? {placement: intent.placement} : {}),
                    ...(intent.location ? {location: intent.location} : {}),
                    ...(listContext ? {listContext} : {})
                }
            ];
        }

        return [];
    });
};

const readIntents = (key: string): IStoredPropertyIntent[] => {
    const value = safeGetItem(key);

    if (!value) {
        return [];
    }

    try {
        return parsePropertyIntents(JSON.parse(value), Date.now());
    } catch {
        return [];
    }
};

const setPendingIntent = (key: string, id: number, meta?: Omit<IPendingFavouritePropertyIntent, "id">) => {
    const now = Date.now();
    const intents = readIntents(key).filter((intent) => !isPendingExpired(intent.createdAt, now));

    if (intents.some((intent) => intent.id === id)) {
        return;
    }

    safeSetItem(key, JSON.stringify([...intents, {id, ...meta, createdAt: now}]));
};

const consumePendingIntents = (key: string, maxAgeMs?: number): IPendingFavouritePropertyIntent[] => {
    const now = Date.now();
    const intents = readIntents(key);

    safeRemoveItem(key);

    return intents.filter((intent) => maxAgeMs == null || !isPendingExpired(intent.createdAt, now, maxAgeMs)).map(toPublicPropertyIntent);
};

export const setPendingFavouritePropertyId = (propertyId: number, meta?: Omit<IPendingFavouritePropertyIntent, "id">) => {
    setPendingIntent(FAVOURITE_PROPERTY_KEY, propertyId, meta);
};

export const consumePendingFavouritePropertyIntents = (maxAgeMs?: number): IPendingFavouritePropertyIntent[] => {
    return consumePendingIntents(FAVOURITE_PROPERTY_KEY, maxAgeMs);
};

export const consumePendingFavouritePropertyIds = (maxAgeMs?: number): number[] => {
    return consumePendingFavouritePropertyIntents(maxAgeMs).map((intent) => intent.id);
};

export const setPendingFavouriteOfferId = (offerId: number, meta?: Omit<IPendingFavouriteOfferIntent, "id">) => {
    setPendingIntent(FAVOURITE_OFFER_KEY, offerId, meta);
};

export const consumePendingFavouriteOfferIntents = (maxAgeMs?: number): IPendingFavouriteOfferIntent[] => {
    return consumePendingIntents(FAVOURITE_OFFER_KEY, maxAgeMs).map((intent) => ({
        id: intent.id,
        ...(intent.placement ? {placement: intent.placement} : {}),
        ...(intent.location ? {location: intent.location} : {})
    }));
};

export const consumePendingFavouriteOfferIds = (maxAgeMs?: number): number[] => {
    return consumePendingFavouriteOfferIntents(maxAgeMs).map((intent) => intent.id);
};

export const redirectToFavouritesList = (url: string) => {
    window.location.assign(url);
};
